import React, { ReactNode, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TimeSpan } from '../utility/time-span';
import { useAuthStore } from '@/zustand/auth/store';
import { useMutationProvider } from '@/hooks';
import { parseJwt } from '@/utility';
import NotificationManager from '@/managers/notification-manager.tsx';
import { useApi } from '@/api/useApi';
import { requestNotificationPermission } from '@/utility/permission.ts';
import useDeviceInfo from '@/hooks/use-device-info.tsx';
import { RefreshTokenRequest } from '@dopingtech/live_expert_bff_api_ts';

dayjs.extend(utc);

interface TokenData {
	exp?: number;
	resource_access?: {
		'live-expert-applicant'?: {
			roles: string[];
		};
	};
}

interface AuthProviderProps {
	children: ReactNode;
	refreshInterval?: number;
}

const DEFAULT_REFRESH_INTERVAL = 5;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, refreshInterval = DEFAULT_REFRESH_INTERVAL }) => {
	const { LoginControllerApi, AuthControllerApi } = useApi();
	const { setData: setAuth, clearData: clearAuth, data: auth } = useAuthStore();
	const timerRef = useRef<NodeJS.Timeout | null>(null);
	const { deviceRequest } = useDeviceInfo();

	const handleLogout = () => {
		NotificationManager.notifyRefreshTokenError();
		clearAuth();
		localStorage.clear();
		cleanupListeners();
	};

	const cleanupListeners = () => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
		document.removeEventListener('visibilitychange', handleVisibilityChange);
	};

	const refreshMutationApplicant = useMutationProvider((refreshToken: string) => AuthControllerApi.refreshToken({ refreshToken }), {
		onError: handleLogout,
		shouldMutate: Boolean(auth?.access_token)
	});

	const refreshMutation = useMutationProvider((body: RefreshTokenRequest) => LoginControllerApi.refreshToken(body), {
		onError: handleLogout,
		shouldMutate: Boolean(auth?.access_token)
	});

	const isExpertApplicant = (tokenData: TokenData): boolean => {
		return Boolean(tokenData?.resource_access?.['live-expert-applicant']?.roles.includes('Expert_Applicant'));
	};

	const refreshToken = async (refreshToken: string) => {
		const tokenData = parseJwt(auth?.access_token) as TokenData;
		return isExpertApplicant(tokenData)
			? refreshMutationApplicant.mutateAsync(refreshToken)
			: refreshMutation.mutateAsync({
					refreshToken: refreshToken,
					deviceRequest: deviceRequest
				});
	};

	const controlAuthExpire = async (): Promise<void> => {
		if (!auth?.access_token) return;

		try {
			const tokenData = parseJwt(auth.access_token) as TokenData;
			if (!tokenData?.exp) return;

			const expire = dayjs.unix(tokenData.exp);
			const expireMinuteDiff = expire.diff(dayjs().utc(), 'minute');

			if (expireMinuteDiff <= refreshInterval) {
				const result: any = await refreshToken(auth.refresh_token);
				if (result?.data) {
					setAuth(result.data);
				}
			}
		} catch (error) {
			console.error('Token refresh failed:', error);
			handleLogout();
		}
	};

	const handleVisibilityChange = () => {
		if (document.visibilityState === 'visible') {
			if (timerRef.current) {
				clearInterval(timerRef.current);
			}
			controlAuthExpire();
			timerRef.current = setInterval(controlAuthExpire, TimeSpan.minutes.one);
		}
	};

	useEffect(() => {
		if (auth?.access_token && deviceRequest?.deviceCode) {
			controlAuthExpire();
			timerRef.current = setInterval(controlAuthExpire, TimeSpan.minutes.one);
			document.addEventListener('visibilitychange', handleVisibilityChange);
			requestNotificationPermission();
			return cleanupListeners;
		}
		cleanupListeners();
	}, [auth?.access_token, deviceRequest?.deviceCode]);

	return <>{children}</>;
};

export default AuthProvider;
